/**
 * Season utility functions for seasonal availability feature
 * Requirements: 1.2, 1.3, 1.4
 */

import type { SeasonConfigItem, SeasonPeriod } from "../types/season";

/**
 * Parse API response items into SeasonPeriod object
 * Returns null for invalid or missing data
 *
 * Requirements: 1.2
 */
export function parseSeasonConfig(
  items: SeasonConfigItem[],
): SeasonPeriod | null {
  if (!items || !Array.isArray(items)) {
    return null;
  }

  const startItem = items.find((item) => item.config_title === "season_start");
  const endItem = items.find((item) => item.config_title === "season_end");

  if (!startItem || !endItem) {
    return null;
  }

  const seasonStart = new Date(startItem.config_value);
  const seasonEnd = new Date(endItem.config_value);

  // Validate parsed dates
  if (isNaN(seasonStart.getTime()) || isNaN(seasonEnd.getTime())) {
    return null;
  }

  // Validate date range (end should not be before start)
  if (seasonEnd < seasonStart) {
    return null;
  }

  return {
    seasonStart,
    seasonEnd,
  };
}

/**
 * Check if a date is within the season range (inclusive)
 * Compares dates at day level, ignoring time components
 *
 * Requirements: 1.3, 1.4
 */
export function isWithinSeason(
  currentDate: Date,
  seasonStart: Date,
  seasonEnd: Date,
): boolean {
  // Normalize dates to start of day for comparison (ignore time)
  const normalizeToDay = (date: Date): number => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };

  const current = normalizeToDay(currentDate);
  const start = normalizeToDay(seasonStart);
  const end = normalizeToDay(seasonEnd);

  return current >= start && current <= end;
}
